import React, { useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { FaEdit, FaTrash, FaEye, FaShareAlt, FaTimes } from "react-icons/fa";
import { deleteNews } from "../../../services/newsServices";
import Button from "../../../components/Button/Button";
import NewsFullModal from "./NewsFullModal";
import { toAbsolutePhotoUrl } from "../../../services/newsPhotoServices";

const FALLBACK = "/img/no-image.jpg";

/* Helpers */
const stripHtml = (html = "") => {
  const div = document.createElement("div");
  div.innerHTML = html;
  return (div.textContent || div.innerText || "").trim();
};

const formatDate = (d) => {
  const dt = new Date(d || Date.now());
  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(dt);
};

/** Retire les <script> et "javascript:" et force les liens en safe */
const sanitizeLight = (html = "") => {
  try {
    const doc = new DOMParser().parseFromString(String(html || ""), "text/html");

    // remove scripts
    doc.querySelectorAll("script").forEach((n) => n.remove());

    // clean anchors
    doc.querySelectorAll("a").forEach((a) => {
      const href = a.getAttribute("href") || "";
      if (/^\s*javascript:/i.test(href)) a.removeAttribute("href");
      a.setAttribute("target", "_blank");
      a.setAttribute("rel", "noopener noreferrer");
    });

    return doc.body.innerHTML || "";
  } catch {
    return "";
  }
};

/**
 * Construit un extrait HTML simple pour l’affichage card :
 * - garde les premiers blocs (p, li, h2, h3)
 * - limite le texte total (en chars)
 */
const makeExcerptHtml = (html = "", maxChars = 320, maxBlocks = 4) => {
  const safe = sanitizeLight(html || "");
  if (!safe) return "";

  try {
    const doc = new DOMParser().parseFromString(safe, "text/html");
    const blocks = Array.from(doc.body.querySelectorAll("p,li,h2,h3,blockquote"));
    if (!blocks.length) return safe;

    let out = "";
    let total = 0;
    let used = 0;

    for (const el of blocks) {
      if (used >= maxBlocks) break;

      const txt = (el.textContent || "").trim();
      if (!txt) continue;

      // coupe au maxChars
      const remaining = maxChars - total;
      if (remaining <= 0) break;

      let chunk = txt;
      let cut = false;
      if (txt.length > remaining) {
        chunk = txt.slice(0, Math.max(0, remaining - 1)).trimEnd();
        cut = true;
      }

      // On reconstruit un HTML propre et simple en <p>
      // (évite de casser des balises quill)
      out += `<p>${chunk}${cut ? "…" : ""}</p>`;
      total += chunk.length;
      used += 1;

      if (cut) break;
    }

    return out;
  } catch {
    return "";
  }
};

export default function NewsCard({ news, canModify, onDeleted, onEdited, onEdit }) {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const createdAt = news.created_at || news.createdAt || news.updated_at || news.updatedAt;

  const coverSrc = useMemo(
    () => toAbsolutePhotoUrl(news.image_url || FALLBACK),
    [news.image_url]
  );

  // ✅ Extrait HTML (mise en forme conservée)
  const excerptHtml = useMemo(() => {
    const html = news.content || "";
    const ex = makeExcerptHtml(html, 360, 4);
    return ex || `<p class="italic text-slate-500">Contenu vide.</p>`;
  }, [news.content]);

  const share = async () => {
    try {
      const url = `${window.location.origin}${window.location.pathname}#news-${news.id}`;
      await navigator.clipboard.writeText(url);
      alert("Lien copié dans le presse-papiers.");
    } catch {
      alert("Impossible de copier le lien.");
    }
  };

  const onImgError = (e) => {
    if (e?.currentTarget) e.currentTarget.src = FALLBACK;
  };

  const remove = async () => {
    try {
      await deleteNews(news.id);
      onDeleted?.();
      onEdited?.(); // si tu utilises aussi ce callback ailleurs
    } catch (error) {
      console.error("Suppression actualité échouée", error);
      alert("Erreur lors de la suppression.");
    } finally {
      setConfirming(false);
    }
  };

  return (
    <article
      id={`news-${news.id}`}
      className="group rounded-2xl overflow-hidden bg-white/80 dark:bg-slate-900/60 backdrop-blur shadow ring-1 ring-slate-200/70 dark:ring-slate-800/70 flex flex-col h-full"
    >
      <div
        className="relative overflow-hidden bg-slate-50 dark:bg-slate-900"
        style={{ aspectRatio: "16 / 9" }}
      >
        <img
          src={coverSrc}
          alt={news.title || "Illustration"}
          className="w-full h-full object-cover transition-transform duration-300 ease-out will-change-transform group-hover:scale-[1.02]"
          loading="lazy"
          onError={onImgError}
          onClick={() => setOpen(true)}
          role="button"
          title="Ouvrir l’article"
        />
        <span className="pointer-events-none absolute inset-0 bg-black/0 transition-opacity duration-300 group-hover:bg-black/5" />
      </div>

      <div className="p-4 flex flex-col gap-3 flex-1">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-slate-900 dark:text-white text-base sm:text-lg font-semibold leading-tight mb-2">
            {news.title || "Sans titre"}
          </h3>

          {!news.is_active && (
            <span className="shrink-0 inline-flex items-center rounded-full px-2 py-1 text-[11px] font-semibold bg-amber-100/80 dark:bg-amber-900/40 ring-1 ring-amber-200 dark:ring-amber-800 text-amber-700 dark:text-amber-300">
              Inactif
            </span>
          )}
        </div>

        <div className="text-[12px] text-slate-600 dark:text-slate-300">
          Publié le <span className="font-bold">{formatDate(createdAt)}</span>
          {news.author && (
            <>
              {" "}
              par{" "}
              <span className="font-bold">
                {news.author?.prenom} {news.author?.nom}
              </span>
            </>
          )}
        </div>

        {/* ✅ Contenu HTML (extrait) */}
        <div
          className="
            text-sm text-slate-700 dark:text-slate-300
            leading-relaxed
            overflow-hidden
            [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:6]
          "
          dangerouslySetInnerHTML={{ __html: excerptHtml }}
        />

        <div className="mt-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-2 text-sm font-semibold text-sky-700 hover:text-sky-900 dark:text-sky-400 dark:hover:text-sky-300 focus:outline-none focus:ring-2 focus:ring-violet-400 rounded-lg px-1.5 py-1"
              title="Lire l’article"
            >
              <FaEye /> Lire
            </button>

            <button
              type="button"
              onClick={share}
              className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white focus:outline-none focus:ring-2 focus:ring-violet-400 rounded-lg px-1.5 py-1"
              title="Copier le lien"
            >
              <FaShareAlt /> Partager
            </button>
          </div>

          {canModify && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => onEdit?.(news)}
                title="Éditer"
                aria-label="Éditer"
                className="w-9 h-9 grid place-items-center rounded-lg ring-1 ring-slate-200 dark:ring-slate-700 text-slate-600 dark:text-slate-300 hover:ring-sky-300 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-400 transition"
              >
                <FaEdit className="text-sm" />
              </button>

              <button
                type="button"
                onClick={() => setConfirming(true)}
                title="Supprimer"
                aria-label="Supprimer"
                className="w-9 h-9 grid place-items-center rounded-lg ring-1 ring-slate-200 dark:ring-slate-700 text-rose-600 hover:ring-rose-300 hover:text-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-400 transition"
              >
                <FaTrash className="text-sm" />
              </button>
            </div>
          )}
        </div>
      </div>

      {open && <NewsFullModal news={news} onClose={() => setOpen(false)} />}

      {confirming &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-3 backdrop-blur-sm"
            onClick={(e) => e.currentTarget === e.target && setConfirming(false)}
            role="dialog"
            aria-modal="true"
            aria-label="Confirmer la suppression"
          >
            <div
              className="w-full max-w-md rounded-2xl overflow-hidden shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200 dark:ring-slate-800"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-4 py-3 bg-gradient-to-r from-rose-600 via-pink-600 to-orange-500 text-white flex items-center justify-between">
                <h4 className="font-semibold">Supprimer cette actualité ?</h4>
                <button
                  onClick={() => setConfirming(false)}
                  className="p-2 rounded-full hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/70"
                  aria-label="Fermer"
                >
                  <FaTimes />
                </button>
              </div>

              <div className="p-4 sm:p-6 space-y-3">
                <p className="text-sm text-slate-700 dark:text-slate-300">
                  Cette action est définitive.
                </p>
                <div className="flex items-center justify-end gap-2">
                  <Button
                    type="button"
                    text="Annuler"
                    onClick={() => setConfirming(false)}
                    className="rounded-xl"
                  />
                  <Button
                    type="button"
                    text="Supprimer"
                    onClick={remove}
                    className="rounded-xl"
                  />
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </article>
  );
}
